// Built-in surface layers of the view core: the hex outline tone shift, the highlight glow and the
// hover outline. Everything else (borders, lens, fog) belongs to its own module.

import * as THREE from 'three/webgpu';
import { float, max, mix, sin, smoothstep, uniform, vec3 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { TERRAIN } from '../core/schema.ts';
import type { SurfaceCtx, SurfaceLayer } from './surface.ts';
import { rgb } from './tsl/color.ts';

type F = Node<'float'>;

// Hex edges: Civ hexes read through a slight tone shift at the edges, not grid lines.
const EDGE_WIDTH = 0.06;
const EDGE_DARKEN = 0.07;
const EDGE_FADE_PX = [0.05, 0.02] as const; // world units per pixel: faded above, full below

// Highlight: an emissive gold rim on the hex outline (at least HIGHLIGHT_RIM_PX pixels wide, however
// far the camera, and never thinner than HIGHLIGHT_RIM_MIN up close) pulsing between HIGHLIGHT_PULSE,
// over a soft inner fill. Both feed the emissive channel, so bloom lifts them off the ground.
const HIGHLIGHT = '#ffd36b';
const HIGHLIGHT_RIM_PX = 2.5;
const HIGHLIGHT_RIM_MIN = 0.06; // world units
const HIGHLIGHT_RIM_MAX = 0.45; // far out the rim fills the hex
const HIGHLIGHT_PULSE = [0.6, 1.0] as const;
const HIGHLIGHT_PULSE_HZ = 1.2;
const HIGHLIGHT_FILL = 0.15;
const HIGHLIGHT_RIM_TINT = 0.85;
const HIGHLIGHT_RIM_GLOW = 1.1;
const HIGHLIGHT_FILL_GLOW = 0.1;
const HIGHLIGHT_RIM_SOFT = 0.5; // inner edge of the rim, as a share of its width

const HOVER = '#fff4d6';
const HOVER_LINE = [0.075, 0.025] as const;

export function hexEdgeLayer(): SurfaceLayer {
  return {
    name: 'hex-edges',
    order: 0,
    color(ctx: SurfaceCtx) {
      // Land only: the sea floor seen through water keeps no grid.
      const land = ctx.data.terrainAt(ctx.hex).terrain.greaterThan(TERRAIN.LAKE + 0.5).select(float(1), float(0));
      const fade = smoothstep(EDGE_FADE_PX[0], EDGE_FADE_PX[1], ctx.px).mul(land);
      const d = ctx.hex.minEdge;
      const dark = smoothstep(EDGE_WIDTH, 0, d).mul(EDGE_DARKEN);
      return ctx.color.mul(float(1).sub(dark.mul(fade)));
    },
  };
}

/** Gold rim and soft fill on hexes set by MapView.highlight(). */
export function highlightLayer(): SurfaceLayer {
  const on = (ctx: SurfaceCtx): F => ctx.data.markAt(ctx.hex).x;
  const rim = (ctx: SurfaceCtx): F => {
    const width = max(float(HIGHLIGHT_RIM_MIN), ctx.px.mul(HIGHLIGHT_RIM_PX)).min(HIGHLIGHT_RIM_MAX);
    return smoothstep(width, width.mul(HIGHLIGHT_RIM_SOFT), ctx.hex.minEdge).mul(on(ctx));
  };
  return {
    name: 'highlight',
    order: 900,
    color(ctx) {
      const tint = on(ctx).mul(HIGHLIGHT_FILL).add(rim(ctx).mul(HIGHLIGHT_RIM_TINT)).min(1);
      return mix(ctx.color, rgb(HIGHLIGHT), tint);
    },
    emissive(ctx) {
      const wave = sin(ctx.time.mul(Math.PI * 2 * HIGHLIGHT_PULSE_HZ)).mul(0.5).add(0.5);
      const pulse = mix(float(HIGHLIGHT_PULSE[0]), float(HIGHLIGHT_PULSE[1]), wave);
      const glow = rim(ctx).mul(pulse).mul(HIGHLIGHT_RIM_GLOW).add(on(ctx).mul(HIGHLIGHT_FILL_GLOW));
      return rgb(HIGHLIGHT).mul(glow);
    },
  };
}

export interface HoverLayer {
  layer: SurfaceLayer;
  /** Set the hovered hex (col, row), or null. */
  set(cell: { col: number; row: number } | null): void;
}

/** Thin warm outline around the hovered hex. */
export function hoverLayer(): HoverLayer {
  const cell = uniform(new THREE.Vector2(-1, -1));
  const line = (ctx: SurfaceCtx): F => {
    const same = ctx.hex.col.equal(cell.x).and(ctx.hex.row.equal(cell.y));
    const edge = smoothstep(HOVER_LINE[0], HOVER_LINE[1], ctx.hex.minEdge);
    return edge.mul(same.select(float(1), float(0)));
  };
  return {
    layer: {
      name: 'hover',
      order: 910,
      color(ctx) {
        return mix(ctx.color, rgb(HOVER), line(ctx).mul(0.5));
      },
      emissive(ctx) {
        return vec3(rgb(HOVER)).mul(line(ctx).mul(0.35));
      },
    },
    set(c) {
      cell.value.set(c ? c.col : -1, c ? c.row : -1);
    },
  };
}
